import { useContext, useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import { useWeb3React } from "@web3-react/core";
import { commify, parseEther } from "@ethersproject/units";
import {
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Box,
  Flex,
  Text,
  Spinner,
  Button as CButton,
  UnorderedList,
  ListItem,
  SimpleGrid,
  GridItem,
  useDisclosure,
} from "@chakra-ui/react";
import { FiChevronRight } from "react-icons/fi";
import { AiOutlineInfoCircle } from "react-icons/ai";
// import _keys from "lodash/keys";
// import _isEmpty from "lodash/isEmpty";

// Contexts
import { StakeContext } from "providers/StakeProvider";

// Components
import Alert from "components/Alert";
import Tooltip from "components/Tooltip";
import Button from "./components/CustomButtom";
import ConfirmationModal from "./components/ConfirmationModal";

// Utils
import {
  // getNumbersFromValue,
  isFloat,
  isNumber,
  toFixedWithoutRounding,
  toStringPrecision,
} from "utils/common";

// Constants
import { UNSTAKE_LIMIT_TOOLTIP_TEXT } from "constants/constants";
import {
  CLICK_SWAP_ON_DEX,
  CLICK_UNSTAKE_INPUT_MAX,
  PROCEED_UNSTAKE,
  SUBMIT_UNSTAKE,
} from "constants/analytics";
import {
  defiLabels,
  // defiPrimaryURLKey,
  defiTypeHasAPYMapping,
  defiTypeMapping,
  defiUrls,
} from "constants/defi";
import { BigNumber } from "ethers";

// Types
// import { defiDetails } from "./types";

const UnStake = ({ defiData }: { defiData: any }) => {
  const router = useRouter();

  const { active } = useWeb3React();
  const {
    balLoading: loading,
    walletInfo,
    openWalletModal,
    handleUnstake,
    handleApprove,
    conversionRate,
    conversionFromRate,
    instantPoolMatic,
    isPolygonChainActive,
    maticXAllowance,
    track,
  } = useContext(StakeContext);
  const { xTokenBalance, xTokenSymbol, tokenSymbol, xtokenIcon, icon } =
    walletInfo;

  const [input, setInput] = useState("");
  const [convertedAmount, setConvertedAmount] = useState("0");
  const [error, setError] = useState("");
  const { isOpen, onClose, onOpen } = useDisclosure();

  const maticXLimit = Math.floor(
    +instantPoolMatic * Number(conversionFromRate)
  );

  const inputInWei = input ? parseEther(input) : BigNumber.from(0);

  const isApproved = input
    ? BigNumber.from(maticXAllowance).gt(inputInWei)
    : BigNumber.from(maticXAllowance).gt(0);

  useEffect(() => {
    if (input && isNumber(input)) {
      const value = Number(input) * Number(conversionRate);
      const formattedValue = isFloat(value)
        ? toFixedWithoutRounding(toStringPrecision(value), 4)
        : toStringPrecision(value);

      if (!Number.isNaN(+formattedValue)) {
        setConvertedAmount(formattedValue);
      } else {
        setConvertedAmount("0");
      }
    } else {
      setConvertedAmount("0");
    }
  }, [input, conversionRate]);

  useEffect(() => {
    if (input) {
      const error = checkInput(input);
      setError(error);
    }
  }, [xTokenBalance]);

  const checkInput = (value = input) => {
    let error = "";

    if (!+value || Number.isNaN(+value)) {
      error = "Invalid value. Please provide valid input.";
    } else if (Number(value) > Number(xTokenBalance)) {
      error = "Insufficient funds.";
    } else if (isPolygonChainActive) {
      if (+value > maticXLimit) {
        error = "Please unstake under Unstake Limit.";
      }
    }

    return error;
  };

  const handleChange = (value: string) => {
    const val = value.trim();

    if (isNumber(val) || val === "") {
      const error = checkInput(val);

      if (val.indexOf(".") === -1) {
        setInput(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= 8) {
          setInput(val === "." ? "0." : val);
        }
      }

      setError(error);
    }
  };

  const handleMaxClick = () => {
    track(CLICK_UNSTAKE_INPUT_MAX, {
      unstake_amount_entered: (+xTokenBalance).toString(),
    });
    handleChange((+xTokenBalance).toString());
  };

  const handleClick = () => {
    const error = checkInput();
    if (error) {
      setError(error);
    } else {
      track(SUBMIT_UNSTAKE, {
        amount: input,
      });
      onOpen();
    }
  };

  // const maxYieldPartner = useMemo(
  //   () =>
  //     _keys(defiData).reduce((acc, curr) => {
  //       if (_isEmpty(acc)) {
  //         acc = defiData[curr];
  //       } else {
  //         if (
  //           getNumbersFromValue(defiData[curr].apy) >
  //           getNumbersFromValue(acc.apy)
  //         ) {
  //           const cUrl = defiUrls[curr];
  //           acc = {
  //             key: curr,
  //             tvl: defiData[curr].tvl,
  //             apy: defiData[curr].apy,
  //             partnerName: defiLabels[curr],
  //             link:
  //               cUrl.primary || cUrl[defiPrimaryURLKey[defiTypeMapping[curr]]],
  //             hasAPY: defiTypeHasAPYMapping[defiTypeMapping[curr]],
  //           };
  //         }
  //       }
  //       return acc;
  //     }, {} as defiDetails),
  //   [defiData]
  // );

  const partner = useMemo(
    () => ({
      key: "balancer",
      tvl: defiData.balancer.tvl,
      apy: defiData.balancer.apy,
      partnerName: defiLabels.balancer,
      link: defiUrls.balancer.addLiquidity,
      hasAPY: defiTypeHasAPYMapping[defiTypeMapping.balancer],
    }),
    [defiData]
  );

  return (
    <>
      <Box w="100%" alignItems="center" mb="1rem">
        <Box>
          <Text as="span" fontSize="14px">
            Available Balance :{" "}
          </Text>
          <Text as="span" fontFamily="semi-bold" color="brand.textColor">
            {xTokenBalance}{" "}
            <Text as="span" fontFamily="light" fontSize="14px">
              {xTokenSymbol}
            </Text>
          </Text>
        </Box>
      </Box>
      <InputGroup size="lg" mt="0.5rem" mb="0">
        <InputLeftElement pointerEvents="none">
          <Image
            src={xtokenIcon}
            width="28px"
            height="28px"
            alt="xtoken-icon"
          />
        </InputLeftElement>
        <Input
          type="text"
          borderWidth="1px"
          isInvalid={!!error}
          borderColor={error ? "crimson" : "brand.primary"}
          placeholder={
            active && isPolygonChainActive && !isApproved
              ? "Please approve unstaking"
              : `Enter ${xTokenSymbol} Amount `
          }
          value={input}
          fontSize="1rem"
          onChange={(e) => handleChange(e.target.value)}
          _focus={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
          }}
          _hover={{
            borderColor: error ? "crimson" : "brand.primary",
            borderWidth: "2px",
          }}
          disabled={active && isPolygonChainActive && !isApproved}
          _disabled={{
            opacity: 1,
          }}
        />
        <InputRightElement width="4.5rem">
          <CButton
            bg="brand.backgroundLight"
            h="1.75rem"
            size="sm"
            p={1}
            onClick={handleMaxClick}
            _hover={{
              bg: "brand.primary",
              color: "white",
              outline: "none",
              borderColor: "brand.primary",
            }}
            _focus={{
              outline: "none",
            }}
          >
            Max
          </CButton>
        </InputRightElement>
      </InputGroup>
      <Text
        color="brand.errorColor"
        fontSize="14px"
        my="4px"
        visibility={error ? "visible" : "hidden"}
      >
        {error ? error : "error"}
      </Text>
      <Flex justifyContent="space-between">
        <Flex alignItems="center">
          <Text mr="0.5rem">You will get : </Text>
          <Image src={icon} width="28px" height="28px" alt="token-icon" />
          <Text
            as="span"
            fontSize="20px"
            fontFamily="semi-bold"
            color="brand.textColor"
            ml="0.5rem"
          >
            {convertedAmount}{" "}
            <Text as="span" fontFamily="light" fontSize="1rem">
              {tokenSymbol}
            </Text>
          </Text>
        </Flex>
      </Flex>
      {!isPolygonChainActive && (
        <>
          <Alert type="info" withIcon={false} mt="1rem">
            <UnorderedList spacing={3} fontSize="14px" px="0.5rem">
              <ListItem>
                Unstaking takes around{" "}
                <Text as="span" fontFamily="medium">
                  80 checkpoints (2-3 days).
                </Text>{" "}
              </ListItem>
            </UnorderedList>
          </Alert>
          <Button
            onClick={
              error
                ? undefined
                : active
                ? handleClick
                : () => openWalletModal("Unstake Tab")
            }
            disabled={loading || !!error || (active && !+xTokenBalance)}
            mt="1rem"
          >
            {loading ? (
              <Spinner size="md" color="brand.textLight" />
            ) : (
              <Text>
                {active ? `Unstake ${xTokenSymbol}` : "Connect Wallet"}
              </Text>
            )}
          </Button>
        </>
      )}
      {isPolygonChainActive && (
        <>
          <Alert
            type="info"
            withIcon={false}
            mt="1rem"
            px="1rem"
            gap="0.5rem"
            fontSize={{ base: "14px", lg: "inherit" }}
            _light={{
              bg: "#F0F1F5",
              color: "#000",
            }}
            _dark={{
              bg: "#1F1F1F",
              color: "brand.textLight",
            }}
          >
            <Text fontFamily="medium">
              Unstake Limit :{" "}
              <Text
                as="span"
                color="#A570FF"
                fontFamily="medium"
                _dark={{
                  color: "brand.textLight",
                }}
              >
                {commify(maticXLimit)} {xTokenSymbol}
              </Text>
            </Text>
            <Tooltip label={UNSTAKE_LIMIT_TOOLTIP_TEXT}>
              <Text ml="4px" color="brand.textInactive" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            </Tooltip>
          </Alert>
          <Alert
            type="info"
            withIcon={false}
            mt="0.5rem"
            flexDir="column"
            alignItems="flex-start"
            px="1rem"
            gap="0"
            fontSize={{ base: "12px", lg: "inherit" }}
            _light={{
              bg: "#F0F1F5",
              color: "#000",
            }}
            _dark={{
              bg: "#1F1F1F",
              color: "brand.textLight",
            }}
          >
            <UnorderedList spacing={3} fontSize="14px" px="0.5rem" mt="0">
              {!instantPoolMatic && (
                <ListItem>
                  The available unstaking limit has been reached. The limit will
                  be refreshed in some time. Please try later or swap on DEX for{" "}
                  <Text as="span" fontFamily="medium">
                    instant liquidity
                  </Text>{" "}
                  and{" "}
                  <Text as="span" fontFamily="medium">
                    arbitrage opportunities.
                  </Text>
                </ListItem>
              )}
              <ListItem>
                Withdrawing Matic will take{" "}
                <Text as="span" fontFamily="medium">
                  90 checkpoints (2-3 days)
                </Text>{" "}
                as per Polygon&apos;s native unstaking
              </ListItem>
              <ListItem>
                Swap on DEX for{" "}
                <Text as="span" fontFamily="medium">
                  instant liquidity
                </Text>{" "}
                and{" "}
                <Text as="span" fontFamily="medium">
                  arbitrage opportunities.
                </Text>
              </ListItem>
            </UnorderedList>
          </Alert>
          {active && isApproved ? (
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              gap={{ base: 0, lg: "1rem" }}
            >
              <GridItem>
                <Button
                  rightIcon={<FiChevronRight />}
                  gap={0}
                  borderWidth="1px"
                  fontSize="1.25rem"
                  fontFamily="medium"
                  fontWeight="light"
                  bg={error ? "brand.primary" : "#F7F1FF"}
                  borderColor={error ? "brand.primary" : "#A570FF"}
                  color={error ? "brand.textLight" : "#A570FF"}
                  onClick={() => {
                    track(CLICK_SWAP_ON_DEX);
                    router.push("/liquid-staking/defi");
                  }}
                  _hover={{
                    bg: error ? "brand.primary" : "#F7F1FF",
                    color: error ? "brand.textLight" : "#A570FF",
                    borderColor: error ? "brand.primary" : "#A570FF",
                  }}
                  _dark={{
                    bg: error ? "brand.primary" : "#391B6B",
                    color: "white",
                  }}
                >
                  Swap on Dex
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  rightIcon={<FiChevronRight />}
                  gap={0}
                  fontSize="1.25rem"
                  fontFamily="medium"
                  fontWeight="light"
                  onClick={error ? undefined : handleClick}
                  disabled={loading || !!error || !+xTokenBalance}
                >
                  Unstake {xTokenSymbol}
                </Button>
              </GridItem>
            </SimpleGrid>
          ) : (
            <Button
              rightIcon={<FiChevronRight />}
              onClick={
                !active
                  ? () => openWalletModal("Unstake Tab")
                  : !isApproved
                  ? handleApprove
                  : undefined
              }
              mt="1rem"
              disabled={loading}
            >
              {loading ? (
                <Spinner size="md" color="brand.textLight" />
              ) : (
                <Text>
                  {!active
                    ? "Connect Wallet"
                    : !isApproved && "Approve Unstaking"}
                </Text>
              )}
            </Button>
          )}
        </>
      )}
      <ConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        defiInfo={partner}
        onProceed={() => {
          onClose();
          track(PROCEED_UNSTAKE, {
            amount: input,
          });
          handleUnstake(toFixedWithoutRounding(toStringPrecision(+input), 8));
          setInput("");
        }}
      />
    </>
  );
};

export default UnStake;
